import {
  CurrentCompanyInfoDto,
  CustomModalActionType,
  FormQuestionDto,
  FormSectionDto,
  QuestionConditionalModalDto,
  QuestionConditionTypeEnum,
} from "@green0meter/api-client";
import { FieldValues } from "react-hook-form";
import { isEmpty } from "lodash";

import { isQuestionConditionMet } from "./isQuestionConditionMet";
import { isStepValid } from "./isStepValid";

import { CustomFormSectionDto } from "@components/common/MultistepForm";

export const doesAnswerTriggerModal = (
  question: FormQuestionDto,
  values: FieldValues,
  modal: QuestionConditionalModalDto,
  company?: CurrentCompanyInfoDto,
): boolean => {
  const answerRaw = values[question.code];

  if (company?.hasFullPremium && !modal.applicableForPremium) {
    return false;
  }

  if (!answerRaw || !question.answerOptions || !modal.values?.length) return false;

  const selectedIdsSet = new Set(Array.isArray(answerRaw) ? answerRaw : [answerRaw]);
  const modalValuesSet = new Set(modal.values);

  for (const opt of question.answerOptions) {
    if (selectedIdsSet.has(opt.code) && modalValuesSet.has(opt.code)) return true;
  }

  return false;
};

export const getConditionalSections = (
  formSections: FormSectionDto[],
  values: FieldValues,
  editMode?: boolean,
  currentCompanyInfo?: CurrentCompanyInfoDto,
): CustomFormSectionDto[] => {
  const allQuestions = formSections.flatMap((section) => section.formQuestions);
  const allQuestionsByCode = new Map(allQuestions.map((q) => [q?.code, q]));
  let sectionIndex = 0;

  let stopAll = false;

  return formSections.map((section) => {
    const isRequired =
      section.formQuestions?.some((formQuestion) => formQuestion.isRequired) ||
      false;

    if (editMode) {
      return {
        ...section,
        formQuestions:
          section.formQuestions?.map((question) => ({
            ...question,
            isVisible: true,
            showGetInTouchModal: false,
            questionIndex: question.questionIndex,
          })) || [],
        isVisible: true,
        isMatchingQuery: true,
        isCompleted: false,
        isRequired,
      };
    }

    const questions = section?.formQuestions || [];

    let stopInSection = false;
    let hasActiveQuestions = false;

    const newQuestions = questions.map((question) => {
      if (stopAll || stopInSection) {
        return {
          ...question,
          isVisible: false,
          isMatchingQuery: true,
          showGetInTouchModal: false,
        };
      }

      let isVisible = false;
      let questionShowGetInTouchModal = false;

      if (!question.isVisible) {
        isVisible = false;
      } else if (!question.conditions || isEmpty(question.conditions)) {
        isVisible = true;
      } else {
        isVisible = question.conditions.every((condition) => {
          if (condition.type === QuestionConditionTypeEnum.QUESTION) {
            const target = allQuestionsByCode.get(condition.variable);

            if (!target) return false;

            return isQuestionConditionMet(condition, values, target);
          }

          return true;
        });
      }

      if (question.questionConditionalModals) {
        questionShowGetInTouchModal = question.questionConditionalModals?.some(
          (modal) =>
            modal.actionType === CustomModalActionType.GetInTouch &&
            doesAnswerTriggerModal(question, values, modal, currentCompanyInfo),
        );
      }

      if (isVisible) {
        hasActiveQuestions = true;
      }

      // If the question has a get in touch modal, we should not show any subsequent questions in this section -> set stopInSection to true
      if (questionShowGetInTouchModal) {
        stopInSection = false;
      }

      return {
        ...question,
        isVisible,
        isMatchingQuery: true,
        showGetInTouchModal: questionShowGetInTouchModal,
      };
    });

    // If we hit a "stop" question, all following sections should be hidden -> set stopAll to true
    if (stopInSection) {
      stopAll = false;
    }

    const sectionIsVisible = hasActiveQuestions || stopInSection;

    return {
      ...section,
      sectionIndex: sectionIsVisible ? sectionIndex++ : 0,
      isVisible: sectionIsVisible,
      isMatchingQuery: true,
      isCompleted: isStepValid(section, values),
      isRequired,
      formQuestions: newQuestions,
    };
  });
};
